import { v4 as uuidv4 } from "uuid";
import moment, { Moment } from "moment";
import { PostType } from "../../cores/socialPost/enums/postType";
import { SocialProviderId } from "../../cores/socialPost/config";
import { PostImage } from "../../cores/socialPost/interfaces/postImage";
import { PostVideo } from "../../cores/socialPost/interfaces/postVideo";
import { SocialProvider } from "../../cores/socialPost/interfaces/socialProvider";
import { Media } from "../../cores/socialPost/interfaces/api/get-social-posts";

export interface ContentGridRow {
  id: string;
  date: string;
  platform: string;
  format: string;
  mediaType: "none" | "image" | "video" | "link";
  mediaUrl: string;
  copy: string;
  comments: string;
  status: "pending" | "approved" | "rejected";
  approvalCount: number;
  rejectionCount: number;
  decisionLocked: boolean;
  scheduledToCalendar: boolean;
  scheduledNetwork: string;
}

const PENDING_SESSION_KEY = "wembii.contentGrid.pendingRows";
const rowsStorageKey = (companyId: string | number) =>
  `wembii.contentGrid.rows.${companyId}`;

export const createEmptyContentGridRow = (): ContentGridRow => ({
  id: uuidv4(),
  date: "",
  platform: "instagram",
  format: "post",
  mediaType: "none",
  mediaUrl: "",
  copy: "",
  comments: "",
  status: "pending",
  approvalCount: 0,
  rejectionCount: 0,
  decisionLocked: false,
  scheduledToCalendar: false,
  scheduledNetwork: "",
});

export const serializeContentGrid = (rows: ContentGridRow[]): string => {
  return encodeURIComponent(JSON.stringify(rows));
};

export const deserializeContentGrid = (value: string): ContentGridRow[] => {
  try {
    const parsed = JSON.parse(decodeURIComponent(value));

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((row) => normalizeContentGridRow(row));
  } catch (error) {
    return [];
  }
};

export const normalizeContentGridRow = (row: any): ContentGridRow => ({
  id: String(row?.id ?? uuidv4()),
  date: String(row?.date ?? ""),
  platform: String(row?.platform ?? "instagram"),
  format: String(row?.format ?? "post"),
  mediaType:
    row?.mediaType === "image" ||
    row?.mediaType === "video" ||
    row?.mediaType === "link"
      ? row.mediaType
      : "none",
  mediaUrl: String(row?.mediaUrl ?? ""),
  copy: String(row?.copy ?? ""),
  comments: String(row?.comments ?? ""),
  status:
    row?.status === "approved" || row?.status === "rejected"
      ? row.status
      : "pending",
  approvalCount: Number.isFinite(Number(row?.approvalCount))
    ? Number(row.approvalCount)
    : 0,
  rejectionCount: Number.isFinite(Number(row?.rejectionCount))
    ? Number(row.rejectionCount)
    : 0,
  decisionLocked: Boolean(row?.decisionLocked),
  scheduledToCalendar: Boolean(row?.scheduledToCalendar),
  scheduledNetwork: String(row?.scheduledNetwork ?? ""),
});

export const mapSocialProviderIdToContentGridPlatform = (
  providerId: string
): string => {
  const normalized = String(providerId || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  switch (normalized) {
    case "instagram":
    case "instagramlogin":
      return "instagram";
    case "facebook":
      return "facebook";
    case "linkedin":
    case "linkedinbusiness":
      return "linkedin";
    case "tiktok":
      return "tiktok";
    case "twitter":
    case "x":
      return "x";
    case "youtube":
      return "youtube";
    default:
      return normalized || "instagram";
  }
};

export const mapPostTypeToContentGridFormat = (postType?: string): string => {
  switch (postType) {
    case PostType.REEL:
      return "reel";
    case PostType.VIDEO:
    case PostType.SHORT:
      return "video";
    case PostType.POST:
    default:
      return "post";
  }
};

export const resolveContentGridMedia = (params: {
  images?: PostImage[];
  video?: PostVideo | null;
  selectedMedia?: Media[];
}): { mediaType: ContentGridRow["mediaType"]; mediaUrl: string } => {
  const { images = [], video, selectedMedia = [] } = params;
  if (video?.data) {
    return { mediaType: "video", mediaUrl: String(video.data) };
  }
  if (images[0]?.data) {
    return { mediaType: "image", mediaUrl: String(images[0].data) };
  }
  const firstMedia = selectedMedia[0];
  if (firstMedia?.url) {
    const isVideo =
      String(firstMedia.type || "").toLowerCase().includes("video") ||
      /\.(mp4|mov|webm)(\?|$)/i.test(firstMedia.url);
    return {
      mediaType: isVideo ? "video" : "image",
      mediaUrl: firstMedia.url,
    };
  }
  return { mediaType: "none", mediaUrl: "" };
};

export const buildContentGridRowsFromComposer = (params: {
  text: string;
  socialProviders: SocialProvider[];
  postType?: string;
  images?: PostImage[];
  video?: PostVideo | null;
  selectedMedia?: Media[];
  scheduleDate?: string | Moment | Date | null;
  comments?: string;
}): ContentGridRow[] => {
  const {
    text,
    socialProviders,
    postType,
    images,
    video,
    selectedMedia,
    scheduleDate,
    comments = "",
  } = params;

  const dateValue = scheduleDate
    ? moment(scheduleDate).isValid()
      ? moment(scheduleDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");
  const format = mapPostTypeToContentGridFormat(postType);
  const media = resolveContentGridMedia({ images, video, selectedMedia });
  const providers =
    socialProviders.length > 0
      ? socialProviders
      : [
          {
            id: SocialProviderId.Instagram,
            name: SocialProviderId.Instagram,
            displayName: "Instagram",
          } as SocialProvider,
        ];

  return providers.map((provider) => ({
    ...createEmptyContentGridRow(),
    date: dateValue,
    platform: mapSocialProviderIdToContentGridPlatform(provider.id),
    format,
    mediaType: media.mediaType,
    mediaUrl: media.mediaUrl,
    copy: text || "",
    comments,
    status: "pending",
  }));
};

export const queueContentGridRows = (rows: ContentGridRow[]): void => {
  if (!rows.length) return;
  try {
    sessionStorage.setItem(PENDING_SESSION_KEY, JSON.stringify(rows));
  } catch (error) {
    console.error("No se pudo guardar filas pendientes de la grilla", error);
    throw error;
  }
};

export const consumeQueuedContentGridRows = (): ContentGridRow[] => {
  try {
    const raw = sessionStorage.getItem(PENDING_SESSION_KEY);
    if (!raw) return [];
    sessionStorage.removeItem(PENDING_SESSION_KEY);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((row) => normalizeContentGridRow(row));
  } catch (error) {
    console.error("No se pudieron leer filas pendientes de la grilla", error);
    return [];
  }
};

export const loadPersistedContentGridRows = (
  companyId: string | number | undefined | null
): ContentGridRow[] | null => {
  if (companyId == null || companyId === "") return null;
  try {
    const raw = localStorage.getItem(rowsStorageKey(companyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((row) => normalizeContentGridRow(row));
  } catch (error) {
    console.error("No se pudo cargar la grilla persistida", error);
    return null;
  }
};

export const persistContentGridRows = (
  companyId: string | number | undefined | null,
  rows: ContentGridRow[]
): void => {
  if (companyId == null || companyId === "") return;
  const key = rowsStorageKey(companyId);
  try {
    localStorage.setItem(key, JSON.stringify(rows));
  } catch (error) {
    // Si el medio en base64 es demasiado grande, persistir sin mediaUrl.
    try {
      const lightRows = rows.map((row) => ({
        ...row,
        mediaUrl:
          row.mediaUrl && row.mediaUrl.length > 200_000 ? "" : row.mediaUrl,
      }));
      localStorage.setItem(key, JSON.stringify(lightRows));
    } catch (innerError) {
      console.error("No se pudo persistir la grilla de contenidos", innerError);
    }
  }
};

export const isContentGridRowBlank = (row: ContentGridRow): boolean =>
  !row.copy.trim() &&
  !row.date &&
  !row.mediaUrl &&
  !row.comments.trim() &&
  row.status === "pending" &&
  !row.scheduledToCalendar;
